import { Icon, SectionHeader } from "../../components/public/ui";

const checklist = [
  "Exact timing counters with zero distractions",
  "Identical 38-question reading & listening grid",
  "Authentic Canadian accents & realistic scenarios",
];

const palette = [
  { n: 1, state: "done" },
  { n: 2, state: "done" },
  { n: 3, state: "done" },
  { n: 4, state: "current" },
  ...[5, 6, 7, 8].map((n) => ({ n, state: "todo" })),
  ...[9, 10, 11, 12].map((n) => ({ n, state: "todo-sm" })),
];

const paletteClass: Record<string, string> = {
  done: "p-2 rounded bg-primary text-on-primary font-bold",
  current: "p-2 rounded bg-tertiary text-on-tertiary font-bold",
  todo: "p-2 rounded bg-surface-container-high text-on-surface",
  "todo-sm":
    "p-2 rounded bg-surface-container-high text-on-surface hidden sm:block",
};

const metrics = [
  {
    label: "Reading Comprehension",
    band: "CLB 10",
    bandColor: "text-tertiary",
    bar: "bg-tertiary h-full w-[92%] rounded-full",
    note: "Strengths: Identifying viewpoint & inference",
  },
  {
    label: "Writing Coherence",
    band: "CLB 8",
    bandColor: "text-primary",
    bar: "bg-primary h-full w-[78%] rounded-full",
    note: "Focus needed: Survey topic sentences",
  },
  {
    label: "Listening Recall",
    band: "CLB 9",
    bandColor: "text-secondary",
    bar: "bg-secondary h-full w-[85%] rounded-full",
    note: "Fast speaker context accurately extracted",
  },
  {
    label: "Speaking Pronunciation",
    band: "CLB 8",
    bandColor: "text-accent-warm",
    bar: "bg-accent-warm h-full w-[75%] rounded-full",
    note: "Recommended: 12-min Daily Pitch Drill",
  },
];

export const PlatformFeatures = () => (
  <section className="w-full py-20 bg-surface-container-low/50">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col gap-24">
      <SectionHeader
        className=""
        eyebrow="Why PassEnglishTest"
        title="A Better Way to Prepare for Your English Test"
        description="We eliminated outdated textbooks and generic drills to create a focused, realistic digital exam environment."
      />

      {/* Feature 1: text left, mockup right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 flex flex-col gap-5">
          <div className="w-10 h-10 rounded-lg bg-primary-fixed flex items-center justify-center text-primary">
            <Icon name="devices" className="text-xl" />
          </div>
          <h3 className="font-headline-md text-headline-md text-on-surface">
            Realistic Practice Tests
          </h3>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            Get familiar with the exam experience through practice tests
            engineered to replicate the official computerized interface, pacing,
            question sequences, and scoring rubrics.
          </p>
          <ul className="space-y-2.5 pt-2 list-none p-0 m-0">
            {checklist.map((item) => (
              <li
                key={item}
                className="flex items-center gap-3 text-on-surface font-body-sm"
              >
                <Icon name="check_circle" className="text-tertiary text-base" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7 bg-surface-container-lowest rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="flex items-center justify-between pb-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-primary" />
              <span className="font-label-sm text-label-sm font-bold text-on-surface">
                Simulated Exam Room • Full Mock #4
              </span>
            </div>
            <span className="font-label-sm text-label-sm text-tertiary font-bold bg-tertiary-fixed/30 px-3 py-1 rounded-full">
              Live Simulation
            </span>
          </div>
          <div className="p-4 bg-surface-container rounded-xl flex flex-col gap-3">
            <span className="font-label-sm text-label-sm text-on-surface-variant">
              Question Palette (Reading Test)
            </span>
            <div className="grid grid-cols-8 sm:grid-cols-12 gap-1.5 text-center text-xs">
              {palette.map((q) => (
                <div key={q.n} className={paletteClass[q.state]}>
                  {q.n}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 text-[11px] text-on-surface-variant">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-primary" /> Completed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-tertiary" /> Current
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded bg-surface-container-high" />{" "}
                Unvisited
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Feature 2: mockup left, text right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1 bg-surface-container-lowest rounded-2xl p-6 shadow-xl">
          <div className="flex items-center justify-between pb-4">
            <span className="font-headline-sm text-headline-sm text-on-surface">
              Skill Diagnostic Radar
            </span>
            <span className="font-label-sm text-label-sm text-primary font-bold">
              Updated Today
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {metrics.map((m) => (
              <div
                key={m.label}
                className="p-4 rounded-xl bg-surface-container-low flex flex-col gap-2"
              >
                <div className="flex justify-between items-center">
                  <span className="font-label-sm text-label-sm text-on-surface font-semibold">
                    {m.label}
                  </span>
                  <span
                    className={`font-label-sm text-label-sm font-bold ${m.bandColor}`}
                  >
                    {m.band}
                  </span>
                </div>
                <div className="w-full bg-surface-container-highest h-2 rounded-full overflow-hidden">
                  <div className={m.bar} />
                </div>
                <span className="text-[11px] text-on-surface-variant">
                  {m.note}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col gap-5">
          <div className="w-10 h-10 rounded-lg bg-tertiary-fixed flex items-center justify-center text-tertiary">
            <Icon name="psychology" className="text-xl" />
          </div>
          <h3 className="font-headline-md text-headline-md text-on-surface">
            Personalized Learning Experience
          </h3>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            Focus directly on the weak spots that hold back your overall band
            score. PassEnglishTest highlights exactly which skills need
            attention after every test you take.
          </p>
          <div className="p-4 rounded-xl bg-surface-container-lowest shadow-sm flex items-start gap-3">
            <Icon name="auto_awesome" className="text-primary text-xl" />
            <p className="font-body-sm text-body-sm text-on-surface">
              Spend your study time on the{" "}
              <strong>skills that move your band score</strong> the most,
              instead of re-practicing what you already know.
            </p>
          </div>
        </div>
      </div>

      {/* Feature 3: text left, chart right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 flex flex-col gap-5">
          <div className="w-10 h-10 rounded-lg bg-secondary-fixed flex items-center justify-center text-secondary">
            <Icon name="analytics" className="text-xl" />
          </div>
          <h3 className="font-headline-md text-headline-md text-on-surface">
            Progress Tracking &amp; Score Predictor
          </h3>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            Never wonder where you stand. Watch your projected CLB score trend
            upward as you complete modules, giving you complete clarity before
            booking your test date.
          </p>
          <div className="flex items-center gap-6 pt-2">
            <div>
              <span className="block font-headline-lg text-headline-lg text-primary font-extrabold">
                4
              </span>
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                Test Skills Covered
              </span>
            </div>
            <div>
              <span className="block font-headline-lg text-headline-lg text-tertiary font-extrabold">
                Instant
              </span>
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                AI Score Feedback
              </span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 bg-surface-container-lowest rounded-2xl p-6 shadow-xl flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-label-sm text-label-sm text-on-surface-variant">
                Estimated Overall Score
              </span>
              <div className="font-headline-sm text-headline-sm text-on-surface font-extrabold flex items-center gap-2">
                <span>CLB Level 9 (Canadian Experience Class Ready)</span>
                <Icon name="check_circle" className="text-tertiary text-lg" />
              </div>
            </div>
            <span className="px-3 py-1 rounded-full bg-primary-fixed/40 text-primary font-label-sm text-label-sm font-bold whitespace-nowrap">
              Test Ready
            </span>
          </div>
          <div className="relative w-full h-44 bg-surface-container-low rounded-xl p-4 flex flex-col justify-between">
            <div className="flex justify-between text-xs text-on-surface-variant gap-2">
              <span>Week 1 (Diagnostic: CLB 6.5)</span>
              <span>Week 2 (CLB 7.5)</span>
              <span>Week 3 (CLB 8.5)</span>
              <span>Week 4 (CLB 9.5)</span>
            </div>
            <svg
              className="w-full h-24 overflow-visible"
              fill="none"
              viewBox="0 0 400 100"
              aria-hidden="true"
            >
              {[20, 50, 80].map((y) => (
                <line
                  key={y}
                  className="text-on-surface"
                  stroke="currentColor"
                  strokeDasharray="4 4"
                  strokeOpacity="0.08"
                  x1="0"
                  x2="400"
                  y1={y}
                  y2={y}
                />
              ))}
              <path
                className="text-primary"
                d="M 10 80 Q 120 70 200 45 T 390 15"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="4"
              />
              <circle className="fill-primary" cx="10" cy="80" r="5" />
              <circle className="fill-primary" cx="140" cy="65" r="5" />
              <circle className="fill-secondary" cx="260" cy="35" r="5" />
              <circle className="fill-tertiary" cx="390" cy="15" r="6" />
            </svg>
            <div className="flex items-center justify-between text-xs font-semibold text-primary">
              <span>Baseline Started</span>
              <span className="text-tertiary font-bold">
                Goal Surpassed: Express Entry Draw Ready
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
